import React, { useState } from "react";

import { Typography, Popconfirm, notification } from "antd";
import { DeleteOutlined, RightOutlined } from "@ant-design/icons";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import TooltipTag from "@/components/UI/TooltipTag";
import {
  pillColor,
  textColor,
  tooltipTitle,
} from "@/utils/tableConfigs/VAStatusMapping";
import {
  TableRow,
  LabelTag,
  ResolutionTag,
  AssigneeSelect,
  DateRenderer,
  type AssigneeOption,
} from "./Components";
import DiscrepanciesHistory from "./DiscrepanciesHistory";
import DiscrepancyComments from "./DiscrepancyComments";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { apiFetch } from "@/utils/apiFetch";
import { useScopeParams } from "@/hooks/useScopeParams";

const { Title } = Typography;

type DrawerProps = {
  selectedRecord: any;
  setSelectedRecord: (record: any) => void;
  availableAssignees: AssigneeOption[];
  queryKey: string[];
  assigneeIdToName?: Record<string, string>;
  onAssignmentChange?: () => void;
};

const DiscrepanciesDrawer = ({
  selectedRecord,
  setSelectedRecord,
  availableAssignees,
  queryKey,
  assigneeIdToName,
  onAssignmentChange,
}: DrawerProps) => {
  const { id: trialId } = useScopeParams();
  const [historyRefetchTrigger, setHistoryRefetchTrigger] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const handleAssignmentChange = () => {
    setHistoryRefetchTrigger((prev) => prev + 1);
    onAssignmentChange?.();
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    setDeleting(true);
    try {
      await apiFetch(
        `${import.meta.env.VITE_API_URL}/data/trial/${trialId}/issue_management/${selectedRecord.discrepancy_id}/`,
        { method: "DELETE" },
      );
      notification.success({ message: "Discrepancy deleted" });
      setSelectedRecord(null);
      onAssignmentChange?.();
    } catch (err) {
      notification.error({
        message: "Failed to delete discrepancy",
        description: err instanceof Error ? err.message : "Unknown error",
        duration: 5,
      });
    } finally {
      setDeleting(false);
    }
  };
  const headersArray = [
    { key: "SUBJECT ID", value: selectedRecord?.subject },
    {
      key: "STUDY STATUS",
      value: (
        <TooltipTag
          tagColor={pillColor[selectedRecord?.study_status]}
          textColor={textColor[selectedRecord?.study_status]}
          tooltipTitle={tooltipTitle[selectedRecord?.study_status]}
          tagStyle={{
            padding: "2px 8px",
            borderRadius: "99px",
            fontSize: "14px",
            margin: "0px",
          }}
        >
          {selectedRecord?.study_status}
        </TooltipTag>
      ),
    },
    { key: "SITE", value: selectedRecord?.site },
    { key: "TIMEPOINT", value: selectedRecord?.discrepancy_trial_visit },
  ];

  return (
    <ResizableDrawer
      headerTitle="Subject Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
      headerActions={
        selectedRecord?.source === "manual" ? (
          <Popconfirm
            title="Delete this discrepancy?"
            description="This action cannot be undone."
            onConfirm={handleDelete}
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleting }}
            cancelText="Cancel"
          >
            <button
              type="button"
              aria-label="Delete discrepancy"
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 leading-none text-white"
            >
              <DeleteOutlined className="text-base text-white" />
            </button>
          </Popconfirm>
        ) : undefined
      }
    >
      {selectedRecord && (
        <div className="flex flex-col gap-6">
          {/* Discrepancy Information Section */}
          <section>
            <Title level={5}>Discrepancy Information</Title>
            <div className="bg-white rounded-sm">
              <table className="w-full p-0 px-4  divide-solid divide-y divide-[#F0F0F0] ">
                <tbody className="p-0 divide-y divide-solid  divide-[#F0F0F0]">
                  <TableRow
                    label="Discrepancy"
                    value={selectedRecord.discrepancy_description}
                  />

                  <TableRow
                    label="Details"
                    value={selectedRecord.discrepancy_details}
                  />
                  <TableRow
                    label="Discrepancy Type"
                    value={selectedRecord.discrepancy_type}
                  />
                  <TableRow
                    label="Discrepancy Category"
                    value={selectedRecord.discrepancy_category}
                  />
                  <TableRow
                    label="Discrepancy Subcategory"
                    value={selectedRecord.discrepancy_subcategory}
                  />
                  <TableRow
                    label="Source"
                    value={selectedRecord.discrepancy_data_source}
                  />
                  <TableRow
                    label="Time Open (Days)"
                    value={selectedRecord.discrepancy_days_open}
                  />
                </tbody>
              </table>
            </div>
          </section>

          <CollapsibleSection title="Additional Information">
            <section className="mt-2">
              <div className="bg-white rounded-sm">
                <table className="w-full p-0 px-4  divide-solid divide-y divide-x divide-[#F0F0F0]  border-solid border-[#F0F0F0] border">
                  <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                    <TableRow label="Subject" value={selectedRecord.subject} />
                    <TableRow
                      label="Participant ID"
                      value={selectedRecord.participant_id}
                    />
                    <TableRow label="Site" value={selectedRecord.site} />
                    <TableRow
                      label="Study Status"
                      value={selectedRecord.study_status}
                    />
                    <TableRow
                      label="Timepoint"
                      value={selectedRecord.discrepancy_trial_visit}
                    />
                    <TableRow
                      label="Level"
                      value={selectedRecord.discrepancy_level || "-"}
                    />
                    <TableRow
                      label="Vivo Status"
                      value={selectedRecord.discrepancy_vivo_status || "-"}
                    />
                  </tbody>
                </table>
              </div>
            </section>
          </CollapsibleSection>

          {/* Status Section */}
          <section>
            <Title level={5}>Resolution Status</Title>

            <div className="bg-white rounded-sm ">
              <table className="w-full divide-solid divide-y divide-[#F0F0F0]">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <tr className="divide-x divide-solid divide-[#F0F0F0]">
                    <td className="py-2 px-2 w-[150px]">Resolution</td>
                    <td className="px-2 py-2">
                      <ResolutionTag
                        status={selectedRecord.discrepancy_resolution_status}
                      />
                    </td>
                  </tr>
                  <TableRow
                    label="Discrepancy identification date"
                    value={
                      selectedRecord.discrepancy_identification_datetime ? (
                        <DateRenderer
                          dateString={
                            selectedRecord.discrepancy_identification_datetime
                          }
                        />
                      ) : (
                        "-"
                      )
                    }
                  />
                  <TableRow
                    label="Related data updated date"
                    value={
                      selectedRecord.discrepancy_assignments_last_modified_datetime ? (
                        <DateRenderer
                          dateString={
                            selectedRecord.discrepancy_assignments_last_modified_datetime
                          }
                        />
                      ) : (
                        "-"
                      )
                    }
                  />
                  <TableRow
                    label="Discrepancy resolution date"
                    value={
                      selectedRecord.discrepancy_resolution_datetime ? (
                        <DateRenderer
                          dateString={
                            selectedRecord.discrepancy_resolution_datetime
                          }
                        />
                      ) : (
                        "-"
                      )
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <Title level={5}>Assignments</Title>
            <div className="bg-white rounded-sm">
              <table className="w-full divide-solid divide-y divide-[#F0F0F0]">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <tr className="divide-x divide-solid divide-[#F0F0F0]">
                    <td className="py-2 px-2 w-[150px]">Assigned to</td>
                    <td className="py-2">
                      <AssigneeSelect
                        record={selectedRecord}
                        queryKey={queryKey}
                        availableAssignees={availableAssignees}
                        assigneeIdToName={assigneeIdToName}
                        text={selectedRecord.discrepancy_assignee}
                        setSelectedRecord={setSelectedRecord}
                        onAssignmentChange={handleAssignmentChange}
                      />
                    </td>
                  </tr>
                  <tr className="divide-x divide-solid divide-[#F0F0F0]">
                    <td className="py-2 px-2 w-[150px]">Label</td>
                    <td className="py-2">
                      <LabelTag
                        text={selectedRecord.discrepancy_status}
                        record={selectedRecord}
                        queryKey={queryKey}
                        setSelectedRecord={setSelectedRecord}
                        onAssignmentChange={handleAssignmentChange}
                      />
                    </td>
                  </tr>
                  <tr className="divide-x divide-solid divide-[#F0F0F0]">
                    <td className="py-2 px-2 w-[150px]">
                      Assignment last modified by
                    </td>
                    <td className="py-2 px-3">
                      {selectedRecord.discrepancy_assignment_last_modified_by ? (
                        <AvatarWithInitials
                          name={
                            selectedRecord.discrepancy_assignment_last_modified_by
                          }
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <TableRow
                    label="Assignment last modified date"
                    value={
                      <DateRenderer
                        dateString={
                          selectedRecord.discrepancy_assignments_last_modified_datetime
                        }
                      />
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>

          {/* Comments */}
          <section>
            <Title level={5}>Comments</Title>
            <DiscrepancyComments
              discrepancyId={selectedRecord.discrepancy_id}
              refetchTrigger={historyRefetchTrigger}
            />
          </section>

          {/*Issue History*/}
          <section>
            <Title level={5}>User History</Title>
            <DiscrepanciesHistory
              discrepancyId={selectedRecord.discrepancy_id}
              refetchTrigger={historyRefetchTrigger}
            />
          </section>
        </div>
      )}
    </ResizableDrawer>
  );
};

export default DiscrepanciesDrawer;

const CollapsibleSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="bg-white rounded-sm py-2 px-3">
      <div className="flex items-center justify-between ">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between gap-2 text-black border-none w-full"
        >
          <h5 className="mb-0 text-[16px] font-mcqueenGrotesk font-bold">
            {title}
          </h5>
          <RightOutlined
            className={`transition-transform duration-300 ${
              isOpen ? "rotate-90" : ""
            }`}
          />
        </button>
      </div>
      {isOpen && children}
    </section>
  );
};
